import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './Auth';

/**
 * What the active catalog provider can actually answer.
 *
 * The two providers are not equivalent — TMDB can filter by rating, runtime
 * and language and can count results; Cinemeta can do genre and three sorts
 * and knows no totals. Rather than render dead controls, the browse screens
 * ask this and only draw what the provider behind them can serve.
 */
const CatalogContext = createContext({ ready: false, capabilities: null });

export const CatalogProvider = ({ children }) => {
  const [status, setStatus] = useState({ ready: false, capabilities: null });
  const { user } = useAuth();

  // Keyed on the session rather than on mount. Asking before anyone is signed
  // in gets a 401, and the catch below reads that as "no catalog configured" —
  // which would then survive the login and show the setup notice to someone
  // whose catalog is working perfectly.
  useEffect(() => {
    if (!user) {
      setStatus({ ready: false, capabilities: null });
      return undefined;
    }

    let cancelled = false;
    axios
      .get('/api/catalog/status')
      .then(({ data }) => {
        if (!cancelled) setStatus({ ready: true, ...data.data });
      })
      .catch(() => {
        if (!cancelled) setStatus({ ready: true, configured: false, capabilities: null });
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const value = useMemo(
    () => ({
      ...status,
      // Treat an unknown capability as unsupported: showing a control that
      // silently does nothing is worse than not showing it.
      can: (feature) => Boolean(status.capabilities?.[feature]),
      supportsFilter: (filter) => (status.capabilities?.filters || []).includes(filter),
      supportsSort: (sort) => (status.capabilities?.sorts || []).includes(sort)
    }),
    [status]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
};

export const useCatalogStatus = () => useContext(CatalogContext);
